import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { parseISO, format } from 'date-fns';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { Game, GamesByDateGroup } from '../types/Games';
import { useGameScores } from '@/features/realtime';
import { getTeamLogoUrl, teamLogoSize } from '@/shared/services/getTeamLogoUrl';
import { useVenueAwareTimeFormatter } from '../hooks/useVenueAwareTimeFormatter';
import { LocalSessionState, useAuth } from '@/features/scaffolding/components/AuthContext';
import { Tracking } from '@/shared/services/tracking';
import { LiveDot } from './LiveDot';
import LoadingIndicator from './LoadingIndicator';
import { Stack } from '@mui/material';

/** Extract the primary color hex from a team object on a game */
function getTeamColor(game: Game, side: 'home' | 'away'): string {
  const g = game as any;
  const colorField = side === 'home' ? g.homeTeamColor : g.awayTeamColor;
  if (colorField) return colorField;
  const team = side === 'home' ? g.homeTeam : g.awayTeam;
  return team?.teamColors?.find((tc: any) => tc.type === 'primary')?.hex_color || '#555';
}

interface GamesContentListProps {
  gamesByDate: GamesByDateGroup[];
  dateRefs: React.MutableRefObject<{ [key: string]: HTMLDivElement | null }>;
  allGames: Game[];
  isFetchingPreviousGames: boolean;
  hasMorePreviousGames: boolean;
  isFetchingNextGames: boolean;
  hasMoreNextGames: boolean;
  onLoadPrevious: () => void;
  onLoadNext: () => void;
  activeStickyDate: string | null;
}

const GameRow: React.FC<{ game: Game; liveScore?: any }> = ({ game, liveScore }) => {
  const auth = useAuth();
  const { formatTime } = useVenueAwareTimeFormatter();

  const sessionIsReady = useMemo(() => {
    return auth.sessionState === LocalSessionState.READY;
  }, [auth.sessionState]);

  const gameStatus = game.status || 'recorded';
  const isClickable = ['live', 'recorded'].includes(gameStatus) && sessionIsReady;

  const start = useMemo(() => {
    if (!game.startTimestamp) return new Date();
    return game.startTimestamp instanceof Date
      ? game.startTimestamp
      : parseISO(game.startTimestamp);
  }, [game.startTimestamp]);

  const formattedTime = useMemo(
    () => formatTime(start, { formatType: 'time-only', venueTimezone: null }),
    [start, formatTime],
  );

  const homeScore = liveScore ? liveScore.homeScore : game.homeTeamScore;
  const awayScore = liveScore ? liveScore.awayScore : game.awayTeamScore;

  const StatusChip = () => {
    if (gameStatus === 'live') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <LiveDot size={6} />

          <Typography
            variant="caption"
            sx={{ color: '#f93737', fontWeight: 600, fontSize: '0.7rem' }}
          >
            LIVE
          </Typography>
        </Box>
      );
    }
    if (gameStatus === 'recorded') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography
            variant="caption"
            sx={{ color: 'success.main', fontWeight: 500, fontSize: '0.7rem' }}
          >
            FINAL
          </Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <Link
      to={isClickable ? `/games/${game.uniqueId}` : '#'}
      style={{ textDecoration: 'none', color: 'inherit' }}
      onClick={(e) => {
        if (!isClickable) e.preventDefault();
        Tracking.getInstance().track(gameStatus === 'live' ? 'Join Game' : 'Watch Game', {
          category: 'Games',
          game: `${game.homeTeam.name} vs ${game.awayTeam.name}`,
        });
      }}
    >
      <Box
        sx={{
          opacity: !sessionIsReady ? 0.2 : gameStatus === 'scheduled' ? 0.45 : 1,
          display: 'flex',
          alignItems: 'center',
          py: 1.5,
          px: 2,
          borderRadius: 1,
          cursor: isClickable ? 'pointer' : 'default',
          transition: 'background-color 0.15s ease',
          '&:hover': isClickable ? { backgroundColor: 'action.hover' } : {},
        }}
      >
        {/* Status chip */}
        <Box sx={{ width: 100, flexShrink: 0, textAlign: 'center' }}>
          <StatusChip />
        </Box>

        {/* Away team */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1,
            width: 160,
            flexShrink: 0,
          }}
        >
          <Stack>
            <Typography
              variant="body2"
              sx={{
                fontWeight: gameStatus !== 'scheduled' && awayScore > homeScore ? 700 : 400,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: '#9797A5',
                textAlign: 'right',
                fontFamily: 'Sohne',
                fontSize: '12px',
                lineHeight: '18px',
              }}
            >
              {game.awayTeam.name}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: gameStatus !== 'scheduled' && awayScore > homeScore ? 700 : 400,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textAlign: 'right',
              }}
            >
              {game.awayTeam.abbreviation}
            </Typography>
          </Stack>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 1,
              backgroundColor: getTeamColor(game, 'away'),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={getTeamLogoUrl(game.awayTeam)}
              alt={game.awayTeam.abbreviation || game.awayTeam.name}
              sx={teamLogoSize(32)}
            />
          </Box>
        </Box>

        {/* Score or @ */}
        <Box sx={{ width: 80, flexShrink: 0, textAlign: 'center' }}>
          {gameStatus === 'scheduled' ? (
            <Typography variant="body2" color="text.secondary">
              @
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {awayScore} - {homeScore}
            </Typography>
          )}
        </Box>

        {/* Home team */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: 160, flexShrink: 0 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 1,
              backgroundColor: getTeamColor(game, 'home'),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={getTeamLogoUrl(game.homeTeam)}
              alt={game.homeTeam.abbreviation || game.homeTeam.name}
              sx={teamLogoSize(32)}
            />
          </Box>
          <Stack>
            <Typography
              variant="body2"
              sx={{
                fontWeight: gameStatus !== 'scheduled' && awayScore > homeScore ? 700 : 400,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: '#9797A5',
                textAlign: 'left',
                fontFamily: 'Sohne',
                fontSize: '12px',
                lineHeight: '18px',
              }}
            >
              {game.homeTeam.name}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: gameStatus !== 'scheduled' && awayScore > homeScore ? 700 : 400,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textAlign: 'left',
              }}
            >
              {game.homeTeam.abbreviation}
            </Typography>
          </Stack>
        </Box>

        {/* Time */}
        <Box sx={{ flex: 1, textAlign: 'right' }}>
          {gameStatus === 'live' && liveScore?.period ? (
            <Typography variant="body2" sx={{ color: 'white', fontWeight: 300 }}>
              {liveScore.period} {liveScore.time}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {formattedTime}
            </Typography>
          )}
        </Box>
      </Box>
    </Link>
  );
};

const GamesContentList: React.FC<GamesContentListProps> = ({
  gamesByDate,
  dateRefs,
  allGames,
  isFetchingPreviousGames,
  hasMorePreviousGames,
  isFetchingNextGames,
  hasMoreNextGames,
  onLoadPrevious,
  onLoadNext,
  activeStickyDate,
}) => {
  const { gameScores } = useGameScores();

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Load Previous Games Button */}
      <Box sx={{ textAlign: 'center', mb: 3, display: 'flex', justifyContent: 'center' }}>
        {allGames.length > 0 && (
          <>
            {isFetchingPreviousGames ? (
              <LoadingIndicator message="Loading previous games..." />
            ) : hasMorePreviousGames ? (
              <Button
                variant="outlined"
                startIcon={<KeyboardArrowUpIcon />}
                onClick={onLoadPrevious}
                sx={{ borderRadius: '50px', px: 3 }}
              >
                Load Earlier Games
              </Button>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No more previous games
              </Typography>
            )}
          </>
        )}
      </Box>

      {/* Games grouped by date */}
      {gamesByDate.map(({ date, games }) => {
        const parsedDate = parseISO(date);
        const dayLabel = format(parsedDate, 'EEEE, MMMM d');

        return (
          <Box key={date} sx={{ mb: 4 }}>
            {/* Date header */}
            <Box
              ref={(el) => {
                dateRefs.current[date] = el instanceof HTMLDivElement ? el : null;
              }}
              data-date={date}
              sx={{
                position: 'relative',
                zIndex: date === activeStickyDate ? 10 : 1,
                opacity: activeStickyDate === null || activeStickyDate === date ? 1 : 0,
                transition: 'opacity 0.2s ease-out',
                pointerEvents: activeStickyDate === date ? 'auto' : 'none',
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                {dayLabel}
              </Typography>
            </Box>

            <Divider sx={{ mb: 1 }} />

            {/* Column headers */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                py: 0.5,
                px: 2,
              }}
            >
              <Box sx={{ width: 100, flexShrink: 0 }} />
              <Box sx={{ width: 160, flexShrink: 0, textAlign: 'right' }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    fontSize: '0.65rem',
                    letterSpacing: 0.5,
                  }}
                >
                  Away
                </Typography>
              </Box>
              <Box sx={{ width: 80, flexShrink: 0, textAlign: 'center' }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    fontSize: '0.65rem',
                    letterSpacing: 0.5,
                  }}
                >
                  Score
                </Typography>
              </Box>
              <Box sx={{ width: 160, flexShrink: 0 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    fontSize: '0.65rem',
                    letterSpacing: 0.5,
                  }}
                >
                  Home
                </Typography>
              </Box>
              <Box sx={{ flex: 1, textAlign: 'right' }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    fontSize: '0.65rem',
                    letterSpacing: 0.5,
                  }}
                >
                  Time
                </Typography>
              </Box>
            </Box>

            {/* Game rows */}
            {games.map((game) => {
              let liveScore;
              if (gameScores !== undefined && game.id !== undefined) {
                liveScore = gameScores[game.id];
              }
              return (
                <GameRow
                  key={game.id || `${game.homeTeam.id}-${game.awayTeam.id}-${game.startTimestamp}`}
                  game={game}
                  liveScore={liveScore}
                />
              );
            })}
          </Box>
        );
      })}

      {/* Load Next Games Button */}
      <Box sx={{ textAlign: 'center', mt: 3, display: 'flex', justifyContent: 'center' }}>
        {allGames.length > 0 && (
          <>
            {isFetchingNextGames ? (
              <LoadingIndicator message="Loading next games..." />
            ) : hasMoreNextGames ? (
              <Button
                variant="outlined"
                endIcon={<KeyboardArrowDownIcon />}
                onClick={onLoadNext}
                sx={{ borderRadius: '50px', px: 3 }}
              >
                Load More Games
              </Button>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No more upcoming games
              </Typography>
            )}
          </>
        )}
      </Box>

      {/* No games */}
      {allGames.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h5" color="text.secondary">
            No games found for the selected filters
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default GamesContentList;
